import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import api from "@/lib/api";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import { MODULE_ICONS } from "@/lib/serviceModules";
import { Mail, Phone, User } from "lucide-react";

export default function AdminWaitlist() {
  const { t, lang } = useLang();
  const [counts, setCounts] = useState({});
  const [modules, setModules] = useState([]);
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/service-modules").then(r => setModules(r.data));
    api.get("/waitlist/counts").then(r => setCounts(r.data));
  }, []);

  useEffect(() => {
    const params = filter === "all" ? {} : { module_key: filter };
    api.get("/admin/waitlist", { params }).then(r => setList(r.data));
  }, [filter]);

  const total = Object.values(counts).reduce((s, n) => s + n, 0);

  return (
    <DashboardLayout items={adminNavItems(t)} title="Waitlist">
      <div className="mb-6 max-w-2xl">
        <p className="text-[var(--so-text-soft)]">
          People who signed up to be notified when an upcoming service launches. Strong demand signal for investors.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <button onClick={() => setFilter("all")} data-testid="filter-all" className={`so-card p-4 text-start ${filter === "all" ? "ring-2 ring-[var(--so-orange)]" : ""}`}>
          <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">Total signups</div>
          <div className="text-3xl font-extrabold mt-1 text-[var(--so-orange)]">{total.toLocaleString()}</div>
        </button>
        {modules.filter(m => m.key !== "restaurants").map(m => {
          const Icon = MODULE_ICONS[m.key];
          const c = counts[m.key] || 0;
          const label = lang === "ar" ? m.label_ar : m.label_en;
          return (
            <button
              key={m.key}
              onClick={() => setFilter(m.key)}
              data-testid={`filter-${m.key}`}
              className={`so-card p-4 text-start ${filter === m.key ? "ring-2" : ""}`}
              style={filter === m.key ? { boxShadow: `0 0 0 2px ${m.accent}` } : {}}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${m.accent}1A`, color: m.accent }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold truncate">{label}</div>
              </div>
              <div className="text-2xl font-extrabold mt-2" style={{ color: m.accent }}>{c.toLocaleString()}</div>
            </button>
          );
        })}
      </div>

      <div className="so-card overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--so-border)] font-bold">
          Recent signups {filter !== "all" && `· ${modules.find(m => m.key === filter)?.label_en}`}
        </div>
        {list.length === 0 ? (
          <div className="p-8 text-center text-[var(--so-text-soft)] text-sm" data-testid="empty-waitlist">No signups yet for this filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--so-bg)] text-xs uppercase tracking-wider text-[var(--so-text-soft)]">
              <tr>
                <th className="text-start p-3">Module</th>
                <th className="text-start p-3">Name</th>
                <th className="text-start p-3">Email</th>
                <th className="text-start p-3">Phone</th>
                <th className="text-start p-3">When</th>
              </tr>
            </thead>
            <tbody>
              {list.map(w => {
                const mod = modules.find(m => m.key === w.module_key);
                return (
                  <tr key={w.id} className="border-t border-[var(--so-border)]" data-testid={`waitlist-row-${w.id}`}>
                    <td className="p-3">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${mod?.accent}15`, color: mod?.accent }}>
                        {mod?.label_en || w.module_key}
                      </span>
                    </td>
                    <td className="p-3 font-semibold">{w.name || "—"}</td>
                    <td className="p-3 text-[var(--so-text-soft)]">{w.email}</td>
                    <td className="p-3 text-[var(--so-text-soft)]">{w.phone || "—"}</td>
                    <td className="p-3 text-[var(--so-text-soft)] text-xs">{new Date(w.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}
